import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiError,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiItemsWithMore,
  TuiPagination,
  TuiTabs,
  TuiUnfinishedValidator,
  tuiValidationErrorsProvider,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiInputPasswordModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { NurseService } from "../../../core/services/nurse.service";
import { ShiftService } from "../../../core/services/shift.service";
import { IShift } from "../../../model/interface/shift";
import { catchError, forkJoin, of, switchMap } from "rxjs";
import { SalaryService } from "../../../core/services/salary.service";
import { TuiDay, TuiDayRange } from "@taiga-ui/cdk";
import { ClientService } from "../../../core/services/client.service";
import { ChargesService } from "../../../core/services/charges.service";
import { LeaveService } from "../../../core/services/leave.service";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import { getEmployeeNumber } from "../../../model/interface/nurse";

@Component({
  selector: "app-nurse-profile",
  imports: [
    EnterSubmitDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiDataList,
    TuiDropdown,
    TuiTable,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    TuiButton,
    TuiTextfieldControllerModule,
    TuiFieldErrorPipe,
    TuiError,
    TuiInputModule,
    TuiPagination,
    TuiInputDateRangeModule,
    TuiInputPasswordModule,
  ],
  templateUrl: "./nurse-profile.component.html",
  styleUrl: "./nurse-profile.component.scss",
  providers: [
    DatePipe,
    tuiValidationErrorsProvider({
      required: "This field is required",
      minLength: "Password must be at least 8 characters",
      pattern:
        "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
      passwordMismatch: "Passwords do not match",
    }),
  ],
})
export class NurseProfileComponent implements OnInit {
  nurseId: string = "";
  nurse: any | null = null;
  isLoading: boolean = false;
  upcomingShifts: IShift[] = [];
  completedShifts: IShift[] = [];

  getEmployeeDisplayNumber(): string {
    return getEmployeeNumber(this.nurse);
  }
  salaryRecords: any[] = [];
  protected size: "s" | "l" = "s";
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected open = false;
  isFiltering: boolean = false;
  changePasswordForm: any;
  greetingMessage: string = "";

  upcomingShiftPageIndex = 1;
  shiftPageSize = 5;
  upcomingShiftTotal: number = 0;

  completedShiftPageIndex = 1;
  completedShiftTotal: number = 0;

  salaryPageIndex = 1;
  salaryPageSize = 5;
  salaryTotal: number = 0;

  filterStartDate: string = "";
  filterEndDate: string = "";
  dateRange: TuiDayRange | null = null;

  openMap: { [shiftId: string]: boolean } = {};

  leaveRecords: any[] = [];
  leaveYear = new Date().getFullYear();
  leaveMonth = new Date().getMonth() + 1;
  balance: any | null = null;

  get leaveMonthLabel(): string {
    return new Date(this.leaveYear, this.leaveMonth - 1, 1).toLocaleString(
      "en",
      { month: "long", year: "numeric" }
    );
  }

  get availableCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.entitled ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.entitled ?? 0
      : 0;
    return day + annual;
  }

  get usedCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.used ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.used ?? 0
      : 0;
    return day + annual;
  }

  get remainingCount(): number {
    if (!this.balance) return 0;
    const day = this.balance.dayOffs?.remaining ?? 0;
    const annual = this.balance.annualLeave?.eligible
      ? this.balance.annualLeave?.remaining ?? 0
      : 0;
    return day + annual;
  }

  private readonly dialogs = inject(TuiDialogService);
  private readonly alerts = inject(TuiAlertService);

  constructor(
    private nurseService: NurseService,
    private router: Router,
    private route: ActivatedRoute,
    private shiftService: ShiftService,
    private salaryService: SalaryService,
    private fb: FormBuilder,
    private clientService: ClientService,
    private chargesService: ChargesService,
    private leaveService: LeaveService
  ) {
    this.changePasswordForm = this.fb.group(
      {
        oldPassword: [null, [Validators.required]],
        password: [
          null,
          [
            Validators.required,
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
            ),
          ],
        ],
        confirmPassword: [null, [Validators.required]],
      },
      {
        validator: this.passwordMatchValidator,
      }
    );
  }

  ngOnInit() {
    this.nurseId = this.route.snapshot.paramMap.get("id")!;
    if (this.nurseId) {
      this.loadNurseData();
      // Load both types of shifts
      this.loadUpcomingShifts();
      this.loadCompletedShifts();
      this.loadLeaveRecords();
      this.loadLeaveBalance();
    }
    this.loadSalaryRecords();
    this.setGreetingMessage();

    this.route.queryParams.subscribe((params) => {
      if (params["refresh"] === "true") {
        this.loadNurseData();
        this.loadUpcomingShifts();
        this.loadCompletedShifts();
        this.loadSalaryRecords();
        this.loadLeaveRecords();
        this.loadLeaveBalance();
      }
    });
  }

  loadNurseData(): void {
    this.isLoading = true;
    if (this.nurseId) {
      this.nurseService.getOneNurseById(this.nurseId).subscribe((res: any) => {
        console.log(res);
        this.nurse = res;
        this.isLoading = false;
      });
    }
  }

  // Method to load upcoming shifts
  loadUpcomingShifts(): void {
    const payload = {
      pageIndex: this.upcomingShiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    };

    this.shiftService
      .getPagedShift(payload, this.nurseId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.upcomingShifts = pagedResult.data;
          this.upcomingShiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.upcomingShifts = [];
          this.upcomingShiftTotal = 0;
        }
      });
  }

  loadCompletedShifts(): void {
    const payload = {
      pageIndex: this.completedShiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    };

    this.shiftService
      .getPagedShift(payload, this.nurseId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.completedShifts = pagedResult.data;
          this.completedShiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.completedShifts = [];
          this.completedShiftTotal = 0;
        }
      });
  }

  onFilter(): void {
    if (this.dateRange) {
      const startDate = this.dateRange.from.toLocalNativeDate();
      const endDate = this.dateRange.to.toLocalNativeDate();

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      this.filterStartDate = startDate.toISOString();
      this.filterEndDate = endDate.toISOString();
      this.isFiltering = true;
    } else {
      this.filterStartDate = "";
      this.filterEndDate = "";
      this.isFiltering = false;
    }

    // Reset page indexes if needed
    this.upcomingShiftPageIndex = 1;
    this.completedShiftPageIndex = 1;
    this.loadUpcomingShifts();
    this.loadCompletedShifts();
  }

  resetFilter(): void {
    this.dateRange = null;
    this.filterStartDate = "";
    this.filterEndDate = "";
    this.isFiltering = false;
    this.upcomingShiftPageIndex = 1;
    this.completedShiftPageIndex = 1;
    this.loadUpcomingShifts();
    this.loadCompletedShifts();
  }

  loadSalaryRecords(): void {
    const payload = {
      pageIndex: this.salaryPageIndex,
      pageSize: this.salaryPageSize,
      sortField: "year",
      sortOrder: -1,
    };

    this.shiftService.getMonthlyShiftsByNurseIdPaginated(this.nurseId,payload).subscribe(
      (res: any) => {
        console.log("Paged salary records:", res.data);
        if (res.data && res.data[0]) {
          const pagedResult = res.data[0];
          this.salaryRecords = pagedResult.data;
          this.salaryTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.salaryRecords = [];
          this.salaryTotal = 0;
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  getFormattedDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return "Invalid Date";
    }
    const day = dateObj.getDate();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }

  viewSalaryDetails(nurseId: any, month: any, year: any): void {
    console.log("Salary record details:", month, year);
    this.router.navigate([`nurse/salary/${nurseId}/${month}/${year}`]);
  }

  passwordMatchValidator(
    formGroup: FormGroup
  ): { [key: string]: boolean } | null {
    if (
      formGroup.get("password")?.value !==
      formGroup.get("confirmPassword")?.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  submitChangePassword(): void {
    if (this.changePasswordForm.valid && this.nurseId) {
      this.isLoading = true;
      const payload = {
        id: this.nurseId,
        oldPassword: this.changePasswordForm.value.oldPassword,
        password: this.changePasswordForm.value.password,
      };

      this.nurseService
        .changePassword(payload)
        .pipe(
          catchError((error) => {
            this.isLoading = false;
            this.changePasswordForm.setErrors({ invalid: true });
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            this.changePasswordForm.reset();
            return [];
          })
        )
        .subscribe((response: any) => {
          this.isLoading = false;
          if (response?.oldPwdCheck) {
            this.alerts
              .open(response.message, {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
          } else {
            this.alerts
              .open("Password changed successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.changePasswordForm.reset();
          }
        });
    } else if (this.changePasswordForm.hasError("passwordMismatch")) {
      this.alerts
        .open("Passwords do not match.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    } else {
      this.changePasswordForm.markAllAsTouched();
      this.alerts
        .open("Form is invalid.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    }
  }

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  getMonth(date: Date): number {
    return new Date(date).getMonth() + 1;
  }

  getYear(date: Date): number {
    return new Date(date).getFullYear();
  }

  setGreetingMessage() {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      this.greetingMessage = "Good Morning,";
    } else if (currentHour < 18) {
      this.greetingMessage = "Good Afternoon,";
    } else {
      this.greetingMessage = "Good Evening,";
    }
  }

  onUpcomingShiftPageChange(page: number): void {
    this.upcomingShiftPageIndex = page;
    this.loadUpcomingShifts();
  }

  onCompletedShiftPageChange(page: number): void {
    this.completedShiftPageIndex = page;
    this.loadCompletedShifts();
  }

  getUpcomingShiftPageCount(): number {
    return Math.ceil(this.upcomingShiftTotal / this.shiftPageSize);
  }

  getCompletedShiftPageCount(): number {
    return Math.ceil(this.completedShiftTotal / this.shiftPageSize);
  }

  onSalaryPageChange(page: number): void {
    this.salaryPageIndex = page;
    this.loadSalaryRecords();
  }

  getSalaryPageCount(): number {
    return Math.ceil(this.salaryTotal / this.salaryPageSize);
  }

  toggleDropdown(shiftId: string): void {
    this.openMap[shiftId] = !this.openMap[shiftId];
  }


  getServiceDurationColor(): string {
    if (!this.nurse || !this.nurse.registrationDate) {
      return 'gray';
    }
    const registrationDate = new Date(this.nurse.registrationDate);
    const now = new Date();
  
    let monthsDiff = (now.getFullYear() - registrationDate.getFullYear()) * 12;
    monthsDiff += now.getMonth() - registrationDate.getMonth();
  
    if (now.getDate() < registrationDate.getDate()) {
      monthsDiff--;
    }
  
    if (monthsDiff <= 3) {
      return '#87CEFA';
    } else if (monthsDiff <= 12) {
      return '#FFD700'; 
    } else {
      return '#90EE90'; 
    }
  }

  loadLeaveRecords(): void {
    if (!this.nurseId) return;
    this.leaveService
      .getPagedLeaves({
        pageIndex: 0,
        pageSize: 100,
        filters: {
          nurseId: this.nurseId,
          year: this.leaveYear,
          month: this.leaveMonth,
        },
        sortField: "createdAt",
        sortOrder: -1,
      })
      .subscribe({
        next: (res: any) => {
          this.leaveRecords = res?.data || [];
        },
        error: () => {
          this.leaveRecords = [];
        },
      });
  }

  loadLeaveBalance(): void {
    if (!this.nurseId) return;
    this.leaveService.getBalance(this.nurseId).subscribe({
      next: (res: any) => (this.balance = res?.data),
      error: () => (this.balance = null)
    });
  }

  prevLeaveMonth(): void {
    if (this.leaveMonth === 1) {
      this.leaveMonth = 12;
      this.leaveYear -= 1;
    } else {
      this.leaveMonth -= 1;
    }
    this.loadLeaveRecords();
  }

  nextLeaveMonth(): void {
    if (this.leaveMonth === 12) {
      this.leaveMonth = 1;
      this.leaveYear += 1;
    } else {
      this.leaveMonth += 1;
    }
    this.loadLeaveRecords();
  }
}
